// SPDX-License-Identifier: AGPL-3.0-or-later

import {makeAutoObservable, runInAction} from 'mobx';

export interface InjectedWallet {
	request: (request: {method: string; params?: unknown}) => Promise<unknown>;
	on?: (event: string, callback: (...args: Array<unknown>) => void) => void;
	removeListener?: (event: string, callback: (...args: Array<unknown>) => void) => void;
	providers?: Array<InjectedWallet>;
	ethereum?: InjectedWallet;
	isMetaMask?: boolean;
	isOkxWallet?: boolean;
	isTrust?: boolean;
	isTokenPocket?: boolean;
}

const READ_METHODS = new Set([
	'eth_accounts',
	'eth_chainId',
	'eth_call',
	'eth_getBalance',
	'eth_estimateGas',
	'eth_gasPrice',
	'eth_maxPriorityFeePerGas',
	'eth_getBlockByNumber',
	'eth_blockNumber',
	'eth_getTransactionCount',
	'eth_getTransactionByHash',
	'eth_getTransactionReceipt',
	'eth_getLogs',
	'eth_getCode',
	'net_version',
]);
const INTERACTIVE_METHODS = new Set([
	'eth_requestAccounts',
	'wallet_switchEthereumChain',
	'wallet_addEthereumChain',
	'eth_sendTransaction',
	'personal_sign',
	'eth_signTypedData_v4',
]);
const sameAddress = (left: unknown, right: string | null) =>
	typeof left === 'string' && right !== null && left.toLowerCase() === right.toLowerCase();

export function availableWallets(): Array<{name: string; provider: InjectedWallet}> {
	const scope = window as unknown as Record<string, InjectedWallet | undefined>;
	const ethereum = scope.ethereum;
	const providers = [
		...(ethereum?.providers ?? (ethereum ? [ethereum] : [])),
		scope.okxwallet,
		scope.trustwallet,
		scope.tokenpocket?.ethereum ?? scope.tokenpocket,
		scope.safepal,
		scope.BinanceChain,
	].filter((provider): provider is InjectedWallet => Boolean(provider?.request));
	return [...new Set(providers)].map((provider) => ({
		provider,
		name:
			provider.isOkxWallet || provider === scope.okxwallet
				? 'OKX Wallet'
				: provider.isMetaMask
					? 'MetaMask'
					: provider.isTrust
						? 'Trust Wallet'
						: provider.isTokenPocket
							? 'TokenPocket'
							: provider === scope.safepal
								? 'SafePal'
								: provider === scope.BinanceChain
									? 'Binance Wallet'
									: '浏览器钱包',
	}));
}

class DaoIWalletState {
	account: string | null = null;
	chainId: number | null = null;
	boundAddress: string | null = null;
	error: string | null = null;
	private provider: InjectedWallet | null = null;
	private generation = 0;
	private listeners = new Set<(event: string, args: unknown) => void>();
	private detach: (() => void) | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}
	get matchesBound(): boolean {
		return sameAddress(this.account, this.boundAddress);
	}
	setBoundAddress(address: string | null): void {
		if (address?.toLowerCase() !== this.boundAddress?.toLowerCase()) this.disconnect();
		this.boundAddress = address;
	}
	subscribe(callback: (event: string, args: unknown) => void): () => void {
		this.listeners.add(callback);
		return () => {
			this.listeners.delete(callback);
		};
	}
	private emit(event: string, args: unknown): void {
		for (const listener of this.listeners) listener(event, args);
	}

	async restore(): Promise<void> {
		const address = this.boundAddress;
		for (const {provider} of availableWallets()) {
			try {
				const accounts = await provider.request({method: 'eth_accounts'});
				if (address !== this.boundAddress) return;
				if (Array.isArray(accounts) && sameAddress(accounts[0], address)) {
					await this.connect(provider, false);
					return;
				}
			} catch {
				/* Another injected provider may already be authorized. */
			}
		}
	}

	async connect(provider = availableWallets()[0]?.provider, requestPermission = true): Promise<void> {
		if (!provider) throw new Error('请在钱包浏览器打开，或启用支持的浏览器钱包。');
		this.detach?.();
		this.generation++;
		this.provider = provider;
		const accountsChanged = (...args: Array<unknown>) => {
			const accounts = args[0];
			runInAction(() => {
				this.generation++;
				this.account = Array.isArray(accounts) && typeof accounts[0] === 'string' ? accounts[0] : null;
				this.error = this.account && !this.matchesBound ? '当前钱包与账号绑定地址不同，请切回绑定钱包。' : null;
			});
			this.emit('accountsChanged', [this.matchesBound ? [this.account] : []]);
		};
		const chainChanged = (...args: Array<unknown>) => {
			runInAction(() => {
				this.generation++;
				this.chainId = Number(args[0]);
			});
			this.emit('chainChanged', [args[0]]);
		};
		provider.on?.('accountsChanged', accountsChanged);
		provider.on?.('chainChanged', chainChanged);
		this.detach = () => {
			provider.removeListener?.('accountsChanged', accountsChanged);
			provider.removeListener?.('chainChanged', chainChanged);
		};
		const accounts = await provider.request({method: requestPermission ? 'eth_requestAccounts' : 'eth_accounts'});
		accountsChanged(accounts);
		chainChanged(await provider.request({method: 'eth_chainId'}));
	}

	disconnect(): void {
		this.detach?.();
		this.detach = null;
		this.provider = null;
		this.generation++;
		this.account = null;
		this.chainId = null;
		this.error = null;
		this.emit('accountsChanged', [[]]);
		this.emit('disconnect', [{code: 4900, message: '钱包连接已断开，账号绑定保持不变。'}]);
	}

	async request(method: string, params: unknown, active: boolean): Promise<unknown> {
		if (!READ_METHODS.has(method) && !INTERACTIVE_METHODS.has(method)) throw new Error('不支持的钱包请求。');
		if (INTERACTIVE_METHODS.has(method) && !active) throw new Error('请返回当前业务页面后操作钱包。');
		if (method === 'eth_requestAccounts') {
			if (!this.provider) await this.connect();
			if (!this.matchesBound) throw new Error('请连接账号绑定的钱包。');
			return [this.account];
		}
		if (method === 'eth_accounts') return this.matchesBound ? [this.account] : [];
		if (!this.provider) throw new Error('请先连接账号绑定的钱包。');
		const provider = this.provider;
		const generation = this.generation;
		if (INTERACTIVE_METHODS.has(method)) {
			const accounts = await provider.request({method: 'eth_accounts'});
			if (generation !== this.generation || !Array.isArray(accounts) || !sameAddress(accounts[0], this.boundAddress)) {
				throw new Error('当前钱包地址已变化，请切回绑定钱包后重新操作。');
			}
			const list = Array.isArray(params) ? params : [];
			if (method === 'eth_sendTransaction') {
				const transaction = list[0] as Record<string, unknown> | undefined;
				if (!sameAddress(transaction?.from, this.boundAddress)) throw new Error('交易地址与账号绑定不一致。');
				if (
					transaction?.chainId === undefined ||
					!Number.isSafeInteger(Number(transaction.chainId)) ||
					Number(transaction.chainId) < 1
				)
					throw new Error('交易缺少明确的业务网络，请重新获取报价。');
				const chain = Number(await provider.request({method: 'eth_chainId'}));
				if (Number(transaction.chainId) !== chain) throw new Error('交易网络已变化。');
			}
			if (method === 'personal_sign' && (list.length !== 2 || !sameAddress(list[1], this.boundAddress)))
				throw new Error('签名地址不匹配。');
			if (method === 'eth_signTypedData_v4' && !sameAddress(list[0], this.boundAddress))
				throw new Error('签名地址不匹配。');
			if (generation !== this.generation || provider !== this.provider || !this.matchesBound)
				throw new Error('钱包状态已变化，请重新操作。');
		}
		const result = await provider.request({method, params});
		// A submitted transaction must still be recorded when its wallet changes afterwards.
		if (
			method !== 'eth_sendTransaction' &&
			method !== 'wallet_switchEthereumChain' &&
			method !== 'wallet_addEthereumChain' &&
			generation !== this.generation
		) {
			throw new Error('钱包状态已变化，请重新操作。');
		}
		return result;
	}
}

export const DaoIWallet = new DaoIWalletState();
